import React, { useState, useCallback, useEffect } from 'react';
import ReactFlow, { 
  MiniMap, 
  Controls, 
  Background, 
  ReactFlowProvider,
  applyNodeChanges,
  applyEdgeChanges
} from 'reactflow';
import 'reactflow/dist/style.css';
import { themeStyles, customStyles } from './ArtemisStyles';
import { nodeTypes } from './ArtemisCustomNodes';

// API Optimization nodes - showing evolution of API performance and design
const getInitialNodes = () => {
  const savedPositions = localStorage.getItem('artemisApiOptimizationPositions');
  if (savedPositions) {
    return JSON.parse(savedPositions);
  }
  
  return [
    // User Intent
    {
      id: 'user-intent',
      data: { label: '💬 "Optimize this slow API for better performance and usability"' },
      position: { x: 400, y: 50 },
      style: themeStyles.problemNode,
    },
    
    // Performance Analysis
    {
      id: 'performance-analysis',
      data: { label: '📈 Performance Metrics\n• Avg response: 2.3s\n• P95 latency: 5.8s\n• Throughput: 45 RPS\n• Error rate: 8%' },
      position: { x: 800, y: 50 },
      style: { ...themeStyles.analysisNode, width: 220 },
    },
    
    // Initial Slow API
    {
      id: 'initial-api',
      type: 'collapsibleCode',
      data: { 
        filename: 'slow-api.js',
        code: '// Slow, inefficient API endpoint\n' +
              'app.get("/users/:id/orders", async (req, res) => {\n' +
              '  const userId = req.params.id;\n' +
              '  \n' +
              '  // N+1 query problem\n' +
              '  const user = await User.findById(userId);\n' +
              '  const orders = await Order.findByUserId(userId);\n' +
              '  \n' +
              '  // Inefficient nested queries\n' +
              '  for (let order of orders) {\n' +
              '    order.items = await OrderItem.findByOrderId(order.id);\n' +
              '    for (let item of order.items) {\n' +
              '      item.product = await Product.findById(item.productId);\n' +
              '      item.category = await Category.findById(item.product.categoryId);\n' +
              '    }\n' +
              '  }\n' +
              '  \n' +
              '  // No caching, inefficient serialization\n' +
              '  res.json({ user, orders });\n' +
              '});',
        issues: 'N+1 queries, no caching, no pagination, inefficient joins',
        score: 1.8
      },
      position: { x: 400, y: 200 },
    },
    
    // Scoring System
    {
      id: 'scoring-label',
      data: { label: '⚡ API Performance Metrics' },
      position: { x: 1100, y: 200 },
      style: { ...themeStyles.metricNode, width: 240 },
    },
    {
      id: 'latency-score',
      data: { label: '⏱️ Latency\nAvg: 2.3s\nP95: 5.8s' },
      position: { x: 950, y: 280 },
      style: themeStyles.metricNode,
    },
    {
      id: 'throughput-score',
      data: { label: '🚀 Throughput\n45 RPS\nTarget: 500 RPS' },
      position: { x: 1100, y: 280 },
      style: themeStyles.metricNode,
    },
    {
      id: 'efficiency-score',
      data: { label: '⚙️ Efficiency\nScore: 2/10\nMany redundant calls' },
      position: { x: 1250, y: 280 },
      style: themeStyles.metricNode,
    },
    {
      id: 'load-tests',
      data: { label: '📊 Load Testing\n8% error rate at 100 RPS\n❌ Fails stress tests' },
      position: { x: 1025, y: 380 },
      style: themeStyles.suggestionNode,
    },
    {
      id: 'user-experience',
      data: { label: '👤 User Experience\nSlow page loads\nHigh bounce rate' },
      position: { x: 1175, y: 380 },
      style: themeStyles.suggestionNode,
    },
    
    // Genetic Algorithm Engine
    {
      id: 'genetic-algo',
      data: { label: '🧬 API Evolution Engine\n\n🔄 Mutation: Optimize queries, add caching\n🔗 Crossover: Combine performance strategies\n📈 Selection: Optimize for speed + reliability\n🎲 Population: 25 optimized variants' },
      position: { x: 100, y: 500 },
      style: { ...themeStyles.uniqueFeatureNode, width: 300, height: 140 },
    },
    
    // AI Agents with Prompts
    {
      id: 'ai-agents',
      data: { label: '🤖 API Optimization Specialists' },
      position: { x: 500, y: 500 },
      style: { ...themeStyles.analysisNode, width: 260 },
    },
    {
      id: 'query-agent',
      data: { label: '🗃️ Query Optimization Agent\n\n📝 Prompt:\n"Eliminate N+1 queries using\njoins, eager loading, and\nbatch requests. Implement\nquery batching and efficient\nindex strategies."' },
      position: { x: 200, y: 600 },
      style: { ...themeStyles.llmNode, width: 220, height: 160 },
    },
    {
      id: 'caching-agent',
      data: { label: '⚡ Caching Agent\n\n📝 Prompt:\n"Implement multi-layer caching:\nRedis for queries, CDN for static,\napplication-level caching.\nOptimize cache invalidation\nand cache-aside patterns."' },
      position: { x: 500, y: 600 },
      style: { ...themeStyles.llmNode, width: 220, height: 160 },
    },
    {
      id: 'architecture-agent',
      data: { label: '🏗️ Architecture Agent\n\n📝 Prompt:\n"Redesign API architecture:\nasync processing, pagination,\ncompression, connection pooling,\nand horizontal scaling patterns."' },
      position: { x: 800, y: 600 },
      style: { ...themeStyles.llmNode, width: 220, height: 160 },
    },
    
    // Generation 1 Label
    {
      id: 'gen1-label',
      data: { label: '🌱 Generation 1: Initial Optimizations (Population: 25)' },
      position: { x: 500, y: 800 },
      style: { ...themeStyles.metricNode, width: 400 },
    },
    
    // Generation 1 Variants
    {
      id: 'gen1-1',
      type: 'collapsibleCode',
      data: { 
        filename: 'api-v1a.js (Query Optimization)',
        code: '// Query optimization variant\n' +
              '+ app.get("/users/:id/orders", async (req, res) => {\n' +
              '+   const userId = req.params.id;\n' +
              '+   const page = parseInt(req.query.page) || 1;\n' +
              '+   const limit = parseInt(req.query.limit) || 20;\n' +
              '+   \n' +
              '+   try {\n' +
              '+     // Single optimized query with joins\n' +
              '+     const result = await db.query(`\n' +
              '+       SELECT \n' +
              '+         u.id as user_id, u.name, u.email,\n' +
              '+         o.id as order_id, o.total, o.status, o.created_at,\n' +
              '+         oi.quantity, oi.price,\n' +
              '+         p.name as product_name, p.description,\n' +
              '+         c.name as category_name\n' +
              '+       FROM users u\n' +
              '+       LEFT JOIN orders o ON u.id = o.user_id\n' +
              '+       LEFT JOIN order_items oi ON o.id = oi.order_id\n' +
              '+       LEFT JOIN products p ON oi.product_id = p.id\n' +
              '+       LEFT JOIN categories c ON p.category_id = c.id\n' +
              '+       WHERE u.id = ?\n' +
              '+       ORDER BY o.created_at DESC\n' +
              '+       LIMIT ? OFFSET ?\n' +
              '+     `, [userId, limit, (page - 1) * limit]);\n' +
              '+     \n' +
              '+     const formatted = formatUserOrders(result.rows);\n' +
              '+     res.json({ data: formatted, page, hasMore: result.rows.length === limit });\n' +
              '+   } catch (error) {\n' +
              '+     res.status(500).json({ error: "Internal server error" });\n' +
              '+   }\n' +
              '+ });',
        score: 3.8
      },
      position: { x: 50, y: 900 },
    },
    {
      id: 'gen1-2',
      type: 'collapsibleCode',
      data: { 
        filename: 'api-v1b.js (Caching Strategy)',
        code: '// Caching optimization variant\n' +
              '+ const redis = require("redis");\n' +
              '+ const client = redis.createClient();\n' +
              '+\n' +
              '+ app.get("/users/:id/orders", async (req, res) => {\n' +
              '+   const userId = req.params.id;\n' +
              '+   const cacheKey = `user:${userId}:orders:${req.query.page || 1}`;\n' +
              '+   \n' +
              '+   try {\n' +
              '+     // Check cache first\n' +
              '+     const cached = await client.get(cacheKey);\n' +
              '+     if (cached) {\n' +
              '+       res.setHeader("X-Cache", "HIT");\n' +
              '+       return res.json(JSON.parse(cached));\n' +
              '+     }\n' +
              '+     \n' +
              '+     // Database query with optimizations\n' +
              '+     const [user, orders] = await Promise.all([\n' +
              '+       User.findById(userId),\n' +
              '+       Order.findByUserIdWithIncludes(userId, {\n' +
              '+         include: ["items.product", "items.category"],\n' +
              '+         limit: 20,\n' +
              '+         offset: (req.query.page - 1) * 20\n' +
              '+       })\n' +
              '+     ]);\n' +
              '+     \n' +
              '+     const result = { user, orders };\n' +
              '+     \n' +
              '+     // Cache for 5 minutes\n' +
              '+     await client.setEx(cacheKey, 300, JSON.stringify(result));\n' +
              '+     \n' +
              '+     res.setHeader("X-Cache", "MISS");\n' +
              '+     res.json(result);\n' +
              '+   } catch (error) {\n' +
              '+     res.status(500).json({ error: error.message });\n' +
              '+   }\n' +
              '+ });',
        score: 4.1
      },
      position: { x: 400, y: 900 },
    },
    {
      id: 'gen1-3',
      type: 'collapsibleCode',
      data: { 
        filename: 'api-v1c.js (Architecture Redesign)',
        code: '// Async architecture variant\n' +
              '+ const compression = require("compression");\n' +
              '+ const { promisify } = require("util");\n' +
              '+\n' +
              '+ app.use(compression()); // Response compression\n' +
              '+\n' +
              '+ // Connection pooling\n' +
              '+ const pool = new Pool({\n' +
              '+   connectionString: process.env.DATABASE_URL,\n' +
              '+   max: 20,\n' +
              '+   idleTimeoutMillis: 30000\n' +
              '+ });\n' +
              '+\n' +
              '+ app.get("/users/:id/orders", async (req, res) => {\n' +
              '+   const startTime = Date.now();\n' +
              '+   \n' +
              '+   try {\n' +
              '+     // Parallel processing with streaming\n' +
              '+     const userPromise = getUserOptimized(req.params.id);\n' +
              '+     const ordersPromise = getOrdersStreamOptimized(req.params.id, req.query);\n' +
              '+     \n' +
              '+     const [user, ordersStream] = await Promise.all([\n' +
              '+       userPromise,\n' +
              '+       ordersPromise\n' +
              '+     ]);\n' +
              '+     \n' +
              '+     // Stream response for large datasets\n' +
              '+     res.writeHead(200, {\n' +
              '+       "Content-Type": "application/json",\n' +
              '+       "X-Response-Time": `${Date.now() - startTime}ms`\n' +
              '+     });\n' +
              '+     \n' +
              '+     res.write(`{"user":${JSON.stringify(user)},"orders":[`);\n' +
              '+     \n' +
              '+     let first = true;\n' +
              '+     for await (const order of ordersStream) {\n' +
              '+       if (!first) res.write(",");\n' +
              '+       res.write(JSON.stringify(order));\n' +
              '+       first = false;\n' +
              '+     }\n' +
              '+     \n' +
              '+     res.write("]}");\n' +
              '+     res.end();\n' +
              '+   } catch (error) {\n' +
              '+     res.status(500).json({ error: "Service unavailable" });\n' +
              '+   }\n' +
              '+ });',
        score: 4.3
      },
      position: { x: 750, y: 900 },
    },
    {
      id: 'gen1-more',
      data: { label: '... 22 more variants\nwith different strategies' },
      position: { x: 950, y: 920 },
      style: { ...themeStyles.suggestionNode, width: 150 },
    },
    
    // Evolution Process
    {
      id: 'evolution-1',
      type: 'evolution',
      data: { 
        generation: '1→2',
        description: '🔄 Performance Optimization Fusion',
        scores: {
          'Latency': '2.3s → 180ms',
          'Throughput': '45 → 380 RPS',
          'Efficiency': '2 → 7',
          'Error Rate': '8% → 1.2%'
        }
      },
      position: { x: 400, y: 1100 },
    },
    
    // Generation 2 Label
    {
      id: 'gen2-label',
      data: { label: '🧬 Generation 2: High-Performance Architecture (Best Practices Combined)' },
      position: { x: 500, y: 1250 },
      style: { ...themeStyles.metricNode, width: 500 },
    },
    
    // Generation 2 - Combined Optimizations
    {
      id: 'gen2-1',
      type: 'collapsibleCode',
      data: { 
        filename: 'optimized-api-service.js (Elite Performance)',
        code: '// Ultra-optimized API with all strategies combined\n' +
              '+ import { CacheManager } from "./cache/CacheManager";\n' +
              '+ import { QueryOptimizer } from "./db/QueryOptimizer";\n' +
              '+ import { StreamProcessor } from "./stream/StreamProcessor";\n' +
              '+ import { MetricsCollector } from "./metrics/MetricsCollector";\n' +
              '+\n' +
              '+ class OptimizedUserOrdersAPI {\n' +
              '+   constructor() {\n' +
              '+     this.cache = new CacheManager();\n' +
              '+     this.queryOptimizer = new QueryOptimizer();\n' +
              '+     this.streamProcessor = new StreamProcessor();\n' +
              '+     this.metrics = new MetricsCollector();\n' +
              '+   }\n' +
              '+\n' +
              '+   async getUserOrders(req, res) {\n' +
              '+     const startTime = performance.now();\n' +
              '+     const { id: userId } = req.params;\n' +
              '+     const { page = 1, limit = 20, include = "basic" } = req.query;\n' +
              '+     \n' +
              '+     try {\n' +
              '+       // Multi-level caching strategy\n' +
              '+       const cacheKey = this.cache.generateKey("user-orders", { userId, page, limit, include });\n' +
              '+       const cached = await this.cache.getFromMultiLayer(cacheKey);\n' +
              '+       \n' +
              '+       if (cached) {\n' +
              '+         this.metrics.recordCacheHit("user-orders");\n' +
              '+         return this.sendResponse(res, cached, performance.now() - startTime);\n' +
              '+       }\n' +
              '+       \n' +
              '+       // Optimized parallel queries\n' +
              '+       const [user, ordersCount, orders] = await Promise.all([\n' +
              '+         this.queryOptimizer.getUserById(userId),\n' +
              '+         this.queryOptimizer.getOrdersCount(userId),\n' +
              '+         this.queryOptimizer.getOrdersOptimized(userId, { page, limit, include })\n' +
              '+       ]);\n' +
              '+       \n' +
              '+       const result = {\n' +
              '+         user,\n' +
              '+         orders,\n' +
              '+         pagination: {\n' +
              '+           page: parseInt(page),\n' +
              '+           limit: parseInt(limit),\n' +
              '+           total: ordersCount,\n' +
              '+           hasMore: (page * limit) < ordersCount\n' +
              '+         }\n' +
              '+       };\n' +
              '+       \n' +
              '+       // Intelligent caching with TTL based on data freshness\n' +
              '+       await this.cache.setWithSmartTTL(cacheKey, result, { userId, dataType: "orders" });\n' +
              '+       \n' +
              '+       this.metrics.recordCacheMiss("user-orders");\n' +
              '+       return this.sendResponse(res, result, performance.now() - startTime);\n' +
              '+     } catch (error) {\n' +
              '+       this.metrics.recordError("user-orders", error);\n' +
              '+       return this.sendErrorResponse(res, error);\n' +
              '+     }\n' +
              '+   }\n' +
              '+ }',
        score: 4.8
      },
      position: { x: 200, y: 1350 },
    },
    {
      id: 'gen2-2',
      type: 'collapsibleCode',
      data: { 
        filename: 'query-optimizer.js (DB Layer)',
        code: '// Ultra-efficient database layer\n' +
              '+ export class QueryOptimizer {\n' +
              '+   constructor() {\n' +
              '+     this.connectionPool = new Pool({ max: 50, acquireTimeoutMillis: 1000 });\n' +
              '+     this.preparedStatements = new Map();\n' +
              '+   }\n' +
              '+\n' +
              '+   async getOrdersOptimized(userId, options) {\n' +
              '+     const { page, limit, include } = options;\n' +
              '+     \n' +
              '+     // Dynamic query building based on include level\n' +
              '+     const baseQuery = this.buildOptimizedQuery(include);\n' +
              '+     const params = [userId, limit, (page - 1) * limit];\n' +
              '+     \n' +
              '+     // Use prepared statements for better performance\n' +
              '+     const statement = await this.getPreparedStatement(baseQuery);\n' +
              '+     const result = await statement.execute(params);\n' +
              '+     \n' +
              '+     // Post-process results efficiently\n' +
              '+     return this.transformOrdersResult(result.rows, include);\n' +
              '+   }\n' +
              '+\n' +
              '+   buildOptimizedQuery(includeLevel) {\n' +
              '+     const baseFields = "o.id, o.total, o.status, o.created_at";\n' +
              '+     \n' +
              '+     switch (includeLevel) {\n' +
              '+       case "detailed":\n' +
              '+         return `\n' +
              '+           SELECT ${baseFields},\n' +
              '+                  json_agg(\n' +
              '+                    json_build_object(\n' +
              '+                      \'id\', oi.id,\n' +
              '+                      \'quantity\', oi.quantity,\n' +
              '+                      \'price\', oi.price,\n' +
              '+                      \'product\', json_build_object(\n' +
              '+                        \'id\', p.id,\n' +
              '+                        \'name\', p.name,\n' +
              '+                        \'category\', c.name\n' +
              '+                      )\n' +
              '+                    )\n' +
              '+                  ) as items\n' +
              '+           FROM orders o\n' +
              '+           LEFT JOIN order_items oi ON o.id = oi.order_id\n' +
              '+           LEFT JOIN products p ON oi.product_id = p.id\n' +
              '+           LEFT JOIN categories c ON p.category_id = c.id\n' +
              '+           WHERE o.user_id = $1\n' +
              '+           GROUP BY o.id\n' +
              '+           ORDER BY o.created_at DESC\n' +
              '+           LIMIT $2 OFFSET $3\n' +
              '+         `;\n' +
              '+       default:\n' +
              '+         return `\n' +
              '+           SELECT ${baseFields}\n' +
              '+           FROM orders o\n' +
              '+           WHERE o.user_id = $1\n' +
              '+           ORDER BY o.created_at DESC\n' +
              '+           LIMIT $2 OFFSET $3\n' +
              '+         `;\n' +
              '+     }\n' +
              '+   }\n' +
              '+ }',
        score: 4.7
      },
      position: { x: 600, y: 1350 },
    },
    
    // Final Evolution
    {
      id: 'evolution-2',
      type: 'evolution',
      data: { 
        generation: '2→Final',
        description: '🏆 Production-Grade Performance',
        scores: {
          'Latency': '180ms → 45ms',
          'Throughput': '380 → 1500 RPS',
          'Efficiency': '7 → 10',
          'Error Rate': '1.2% → 0.1%'
        }
      },
      position: { x: 400, y: 1550 },
    },
    
    // Final Output
    {
      id: 'final-output',
      type: 'collapsibleCode',
      data: { 
        filename: 'Enterprise API System',
        code: '// Final optimized API architecture\n' +
              '// ✅ Sub-50ms average response time\n' +
              '// ✅ 1500+ RPS throughput capacity\n' +
              '// ✅ 0.1% error rate under load\n' +
              '// ✅ 99.9% uptime reliability\n' +
              '// ✅ Auto-scaling capabilities\n' +
              '// ✅ Real-time performance monitoring\n' +
              '// ✅ Intelligent caching layers\n' +
              '// ✅ GraphQL-style efficient querying\n' +
              '\n' +
              '🚀 Performance Architecture:\n' +
              '├── Multi-layer caching (Redis + Memory + CDN)\n' +
              '├── Connection pooling & prepared statements\n' +
              '├── Async processing & streaming responses\n' +
              '├── Database query optimization & indexing\n' +
              '├── Response compression & HTTP/2\n' +
              '├── Load balancing & auto-scaling\n' +
              '└── Real-time metrics & alerting\n' +
              '\n' +
              '// Performance improvements:\n' +
              '// • 98% latency reduction (2.3s → 45ms)\n' +
              '// • 3233% throughput increase (45 → 1500 RPS)\n' +
              '// • 99% error rate reduction (8% → 0.1%)\n' +
              '// • 95% cost efficiency improvement',
        score: 4.9
      },
      position: { x: 400, y: 1700 },
    },
    
    // Benefits Box
    {
      id: 'benefits',
      data: { label: '✨ API Benefits\n\n• 98% faster response times\n• 3233% higher throughput\n• 99% fewer errors under load\n• Auto-scaling performance\n• Real-time optimization' },
      position: { x: 800, y: 1650 },
      style: { ...themeStyles.uniqueFeatureNode, width: 250, height: 140 },
    }
  ];
};

const apiEdges = [
  // Initial flow
  { id: 'e1', source: 'user-intent', target: 'performance-analysis', animated: true, style: themeStyles.edgeStyle },
  { id: 'e2', source: 'performance-analysis', target: 'initial-api', style: themeStyles.edgeStyle },
  { id: 'e3', source: 'initial-api', target: 'scoring-label', animated: true, style: themeStyles.edgeStyle },
  
  // Scoring connections
  { id: 'e4', source: 'scoring-label', target: 'latency-score', style: themeStyles.edgeStyle },
  { id: 'e5', source: 'scoring-label', target: 'throughput-score', style: themeStyles.edgeStyle },
  { id: 'e6', source: 'scoring-label', target: 'efficiency-score', style: themeStyles.edgeStyle },
  { id: 'e7', source: 'scoring-label', target: 'load-tests', style: themeStyles.edgeStyle },
  { id: 'e8', source: 'scoring-label', target: 'user-experience', style: themeStyles.edgeStyle },
  
  // Genetic Algorithm
  { id: 'e9', source: 'initial-api', target: 'genetic-algo', style: themeStyles.evolutionEdgeStyle },
  { id: 'e10', source: 'genetic-algo', target: 'ai-agents', animated: true, style: themeStyles.evolutionEdgeStyle },
  
  // AI Agents
  { id: 'e11', source: 'ai-agents', target: 'query-agent', style: themeStyles.edgeStyle },
  { id: 'e12', source: 'ai-agents', target: 'caching-agent', style: themeStyles.edgeStyle },
  { id: 'e13', source: 'ai-agents', target: 'architecture-agent', style: themeStyles.edgeStyle },
  
  // Generation 1
  { id: 'e14', source: 'query-agent', target: 'gen1-1', style: themeStyles.edgeStyle },
  { id: 'e15', source: 'caching-agent', target: 'gen1-2', style: themeStyles.edgeStyle },
  { id: 'e16', source: 'architecture-agent', target: 'gen1-3', style: themeStyles.edgeStyle },
  { id: 'e17', source: 'ai-agents', target: 'gen1-more', style: { ...themeStyles.edgeStyle, strokeDasharray: '5 5' } },
  
  // Evolution to Gen 1
  { id: 'e18', source: 'gen1-1', target: 'evolution-1', style: themeStyles.evolutionEdgeStyle },
  { id: 'e19', source: 'gen1-2', target: 'evolution-1', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'e20', source: 'gen1-3', target: 'evolution-1', style: themeStyles.evolutionEdgeStyle },
  
  // Feedback loops
  { id: 'f1', source: 'latency-score', target: 'evolution-1', style: themeStyles.feedbackEdgeStyle, type: 'smoothstep' },
  { id: 'f2', source: 'load-tests', target: 'evolution-1', style: themeStyles.feedbackEdgeStyle, type: 'smoothstep' },
  { id: 'f3', source: 'user-experience', target: 'evolution-1', style: themeStyles.feedbackEdgeStyle, type: 'smoothstep' },
  
  // Generation 2
  { id: 'e21', source: 'evolution-1', target: 'gen2-1', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'e22', source: 'evolution-1', target: 'gen2-2', animated: true, style: themeStyles.evolutionEdgeStyle },
  
  // Final Evolution
  { id: 'e23', source: 'gen2-1', target: 'evolution-2', style: themeStyles.evolutionEdgeStyle },
  { id: 'e24', source: 'gen2-2', target: 'evolution-2', style: themeStyles.evolutionEdgeStyle },
  
  // Final Output
  { id: 'e25', source: 'evolution-2', target: 'final-output', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'e26', source: 'final-output', target: 'benefits', style: themeStyles.edgeStyle },
];

const ArtemisApiOptimization = () => {
  const [nodes, setNodes] = useState(getInitialNodes());
  const [edges, setEdges] = useState(apiEdges);

  // Add custom styles on mount
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = customStyles;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Save positions whenever nodes change
  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => {
      const updatedNodes = applyNodeChanges(changes, nds);
      const positions = updatedNodes.map(node => ({
        ...node,
        style: node.style,
        type: node.type,
        data: node.data
      }));
      localStorage.setItem('artemisApiOptimizationPositions', JSON.stringify(positions));
      return updatedNodes;
    });
  }, []);

  const onEdgesChange = useCallback((changes) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
  }, []);

  // Reset positions
  const resetPositions = () => {
    localStorage.removeItem('artemisApiOptimizationPositions');
    setNodes(getInitialNodes());
  };

  return (
    <ReactFlowProvider>
      <div style={{ 
        height: '100vh', 
        width: '100%', 
        background: 'linear-gradient(135deg, #F0F4F8 0%, #D9E2EC 100%)',
        position: 'relative'
      }}>
        <h2 style={{ 
          textAlign: 'center', 
          color: '#102A43', 
          padding: '20px', 
          margin: '0',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          fontSize: '26px',
          fontWeight: '700'
        }}>
          ⚡ Artemis API Optimization - Evolving Performance
        </h2>
        
        <div style={{
          position: 'absolute',
          top: '70px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '8px 20px',
          background: 'rgba(16, 42, 67, 0.08)',
          borderRadius: '20px',
          color: '#486581',
          fontSize: '14px',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          zIndex: 100
        }}>
          Genetic algorithms evolve API architecture for maximum performance and scalability
        </div>
        
        <button
          onClick={resetPositions}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            padding: '8px 16px',
            background: '#F4511E',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '12px',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
            zIndex: 100
          }}
        >
          Reset Layout
        </button>
        
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          style={{ height: 'calc(100vh - 120px)', marginTop: '120px' }}
        >
          <Background color="#aaa" gap={16} />
          <MiniMap 
            style={{
              background: 'white',
              border: '1px solid #ccc',
              borderRadius: '8px'
            }}
          />
          <Controls 
            style={{
              background: 'white',
              border: '1px solid #ccc',
              borderRadius: '8px'
            }}
          />
        </ReactFlow>
      </div>
    </ReactFlowProvider>
  );
};

export default ArtemisApiOptimization; 